import { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts/AuthContext';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import PumpList from './PumpList';
import PumpDetailPanel from './PumpDetailPanel';
import PumpEventsModal from './PumpEventsModal';
import BulkUpdateStatusModal from './BulkUpdateStatusModal';
import PoolSelector from './PoolSelector';
import FloatingActionButton from './FloatingActionButton';
import {
  PUMP_STATUS_MENU_ORDER,
  PUMP_STATUS_MENU_ORDER_REPAIRER_FILTER,
  PUMP_STATUS_MENU_ORDER_MAINTENANCE,
  PUMP_STATUS_HIDDEN_MAINTENANCE,
} from './pumpStatusMeta';
import { pumpMatchesNameSearch } from '../../utils/pumpNameSearch';
import './UserFilters.scss';
import './PumpsMainView.scss';

const SCROLL_STORAGE_KEY = 'pumpsListScrollPosition';
const SEARCH_STORAGE_KEY = 'pumpsListSearchTerm';
const STATUS_STORAGE_KEY = 'pumpsListSelectedStatus';
const UNACKNOWLEDGED_STORAGE_KEY = 'pumpsListShowUnacknowledged';

export default function PumpsMainView({ 
  pumps, 
  loading, 
  selectedPool, 
  onPoolSelect
}) {
  const { t } = useTranslation();
  const { pumpId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useAuthContext();
  const isKorjaaja = userRole === 3;
  const isHuolto = userRole === 5;

  const listScrollRef = useRef(null);
  const batchFabRef = useRef(null);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [showOnlyUnacknowledged, setShowOnlyUnacknowledged] = useState(() => {
    return sessionStorage.getItem(UNACKNOWLEDGED_STORAGE_KEY) === 'true';
  });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const stored = sessionStorage.getItem(STATUS_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem(SEARCH_STORAGE_KEY) || '';
  });
  const [selectModeEnabled, setSelectModeEnabled] = useState(false);
  const [selectedPumpIds, setSelectedPumpIds] = useState([]);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [batchFabAnchorRect, setBatchFabAnchorRect] = useState(null);

  const STATUS_OPTIONS = useMemo(
    () => {
      const order = isKorjaaja
        ? PUMP_STATUS_MENU_ORDER_REPAIRER_FILTER
        : isHuolto
          ? PUMP_STATUS_MENU_ORDER_MAINTENANCE
          : PUMP_STATUS_MENU_ORDER;
      return [
        { value: null, label: t('pump_all_statuses') },
        ...order.map((value) => ({
          value,
          label: t(`pump_status_${value}`),
        })),
      ];
    },
    [t, isKorjaaja, isHuolto]
  );

  useEffect(() => {
    if (!isKorjaaja) return;
    if (selectedStatus === 7) {
      setSelectedStatus(null);
      sessionStorage.removeItem(STATUS_STORAGE_KEY);
    }
  }, [isKorjaaja, selectedStatus]);

  useEffect(() => {
    if (!isHuolto) return;
    if (selectedStatus != null && PUMP_STATUS_HIDDEN_MAINTENANCE.includes(selectedStatus)) {
      setSelectedStatus(null);
      sessionStorage.removeItem(STATUS_STORAGE_KEY);
    }
  }, [isHuolto, selectedStatus]);

  const handleExitSelectMode = () => {
    setSelectModeEnabled(false);
    setSelectedPumpIds([]);
  };

  const handleSelectionChange = (ids) => {
    setSelectedPumpIds(ids);
  };


  // Persist filter states to sessionStorage
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    if (value === null) {
      sessionStorage.removeItem(STATUS_STORAGE_KEY);
    } else {
      sessionStorage.setItem(STATUS_STORAGE_KEY, value.toString());
    }
  };

  const handleUnacknowledgedChange = (checked) => {
    setShowOnlyUnacknowledged(checked);
    sessionStorage.setItem(UNACKNOWLEDGED_STORAGE_KEY, checked.toString());
  };

  // Search handler
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    sessionStorage.setItem(SEARCH_STORAGE_KEY, value);
  };

  // Check if URL ends with /events
  useEffect(() => {
    const isEventsRoute = location.pathname.endsWith('/events');
    setShowEventsModal(isEventsRoute);
  }, [location.pathname]);

  // Popup always above the three-dot button (FAB) – also when opened from "Change status" link
  useEffect(() => {
    if (!showBulkUpdateModal) return;
    const id = requestAnimationFrame(() => {
      if (batchFabRef.current) {
        setBatchFabAnchorRect(batchFabRef.current.getBoundingClientRect());
      }
    });
    return () => cancelAnimationFrame(id);
  }, [showBulkUpdateModal]);

  // Save scroll position on scroll
  const handleListScroll = (e) => {
    const position = e.target.scrollTop;
    sessionStorage.setItem(SCROLL_STORAGE_KEY, position.toString());
  };

  // Restore scroll position after pumpId changes
  useLayoutEffect(() => {
    const container = listScrollRef.current;
    const savedPosition = parseInt(sessionStorage.getItem(SCROLL_STORAGE_KEY) || '0', 10);
    
    if (container && savedPosition > 0) {
      container.scrollTop = savedPosition;
      
      setTimeout(() => {
        if (container) {
          container.scrollTop = savedPosition;
        }
      }, 0);
      
      const timeoutId = setTimeout(() => {
        if (container) {
          container.scrollTop = savedPosition;
        }
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [pumpId]);

  const handlePumpClick = (pump) => {
    navigate(`/dashboard/pumps/${pump.id}`);
  };

  const handleBackToList = () => {
    navigate('/dashboard/pumps');
  };

  // Pool + deleted visibility: same idea as PoolsMainView (mine/shaft) — narrow scope first for empty states.
  const pumpsInScope = useMemo(() => {
    return pumps.filter((pump) => {
      const isDeleted = pump.statusCode === 7;
      const matchesDeletedFilter =
        selectedStatus === 7 ? isDeleted : !isDeleted;
      const matchesPool =
        !selectedPool || pump.currentPoolId === selectedPool.id;
      return matchesDeletedFilter && matchesPool;
    });
  }, [pumps, selectedPool, selectedStatus]);

  const filteredPumps = useMemo(() => {
    return pumpsInScope.filter((pump) => {
      const hasUnacknowledged = pump.unacknowledged_comments_count > 0;
      const matchesUnacknowledgedFilter =
        !showOnlyUnacknowledged || hasUnacknowledged;
      const matchesStatusFilter =
        !selectedStatus || pump.statusCode === selectedStatus;
      const matchesSearch = pumpMatchesNameSearch(pump.name, searchTerm);
      return (
        matchesUnacknowledgedFilter &&
        matchesStatusFilter &&
        matchesSearch
      );
    });
  }, [
    pumpsInScope,
    showOnlyUnacknowledged,
    selectedStatus,
    searchTerm,
  ]);

  const emptyListMessage = useMemo(() => {
    if (selectedStatus === 7) return t('pump_empty_no_deleted');
    if (pumpsInScope.length === 0) {
      if (selectedPool) return t('pump_list_empty_pool');
      return t('pump_empty_none_available');
    }
    return t('pool_list_empty_search');
  }, [selectedStatus, selectedPool, pumpsInScope.length, t]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPumpIds(filteredPumps.map((p) => p.id));
    } else {
      setSelectedPumpIds([]);
    }
  };

  const showBulkBar = selectModeEnabled && selectedPumpIds.length > 0;

  const openBulkUpdateModal = () => {
    if (batchFabRef.current) {
      setBatchFabAnchorRect(batchFabRef.current.getBoundingClientRect());
    }
    setShowBulkUpdateModal(true);
  };

  return (
    <div className={`pumps-main-view ${pumpId ? 'has-pump-selected' : ''} ${showBulkBar ? 'has-bulk-selection' : ''}`}>
      <div className="dashboard-toolbar d-flex align-items-center flex-nowrap justify-content-between gap-2 pumps-toolbar">
        <div className="d-flex align-items-center flex-nowrap gap-2 pumps-toolbar-start">
          <div className="d-flex align-items-center flex-nowrap gap-2 pumps-toolbar-dropdowns">
            <div className="d-flex align-items-center flex-shrink-0">
              <PoolSelector onPoolSelect={onPoolSelect} selectedPoolId={selectedPool?.id} />
            </div>
            <div className="status-selector d-flex align-items-center flex-shrink-0">
              <i className="fas fa-filter mr-2" style={{ color: '#666' }} />
              <Dropdown isOpen={statusDropdownOpen} toggle={() => setStatusDropdownOpen(!statusDropdownOpen)}>
                <DropdownToggle caret color="link" className="p-0 text-dark">
                  {selectedStatus ? STATUS_OPTIONS.find(s => s.value === selectedStatus)?.label : t('pump_all_statuses')}
                </DropdownToggle>
                <DropdownMenu container="body" className="pump-status-filter-menu">
                  {STATUS_OPTIONS.map(option => (
                    <DropdownItem
                      key={option.value || 'all'}
                      onClick={() => handleStatusChange(option.value)}
                      active={selectedStatus === option.value}
                    >
                      {option.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          <div className="d-flex align-items-center flex-nowrap gap-2 pumps-toolbar-boolean">
            <button
              type="button"
              className={`role-chip ${selectModeEnabled ? 'active' : ''}`}
              aria-pressed={selectModeEnabled}
              onClick={() => {
                setSelectModeEnabled((prev) => {
                  const next = !prev;
                  if (!next) setSelectedPumpIds([]);
                  return next;
                });
              }}
            >
              {t('pump_select_mode_label')}
            </button>
            <button
              type="button"
              className={`role-chip ${showOnlyUnacknowledged ? 'active' : ''}`}
              aria-pressed={showOnlyUnacknowledged}
              onClick={() => handleUnacknowledgedChange(!showOnlyUnacknowledged)}
            >
              {t('pump_unacknowledged_label')}
            </button>
          </div>
        </div>

        <div className="pump-search-wrapper pumps-toolbar-search toolbar-search-end">
          <i className="fas fa-search pump-search-icon" />
          <input
            type="text"
            className="pump-search-input"
            placeholder={t('pump_search_placeholder')}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchTerm && (
            <button
              className="pump-search-clear"
              onClick={() => handleSearchChange('')}
              type="button"
            >
              ×
            </button>
          )}
        </div>
      </div>
      
      <div className="pumps-master-detail-container">
        {/* Master - Pump List */}
        <div className={`pumps-master ${pumpId ? 'has-selection' : ''}`}>
          <div ref={listScrollRef} onScroll={handleListScroll} className="pumps-list-container">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-2">{t('pump_loading_list')}</p>
              </div>
            ) : filteredPumps.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">{emptyListMessage}</p>
              </div>
            ) : (
              <PumpList 
                pumps={filteredPumps} 
                selectedPumpId={pumpId}
                onPumpClick={handlePumpClick}
                selectModeEnabled={selectModeEnabled}
                selectedPumpIds={selectedPumpIds}
                onSelectionChange={handleSelectionChange}
                onSelectAll={handleSelectAll}
                onOpenBulkUpdate={openBulkUpdateModal}
              />
            )}
          </div>
        </div>

        {/* Detail - Pump Details */}
        <div className={`pumps-detail ${pumpId ? 'show-detail' : ''}`}>
          <div className="panel-wrapper" key={pumpId}>
            <PumpDetailPanel
              pumpId={pumpId}
              onBack={handleBackToList}
            />
          </div>
        </div>
      </div>
      
      {!showBulkBar && <FloatingActionButton />}

      {/* FAB with three dots: opens bubble popup for status change */}
      {showBulkBar && (
        <button
          ref={batchFabRef}
          type="button"
          className="fab pumps-fab-batch"
          onClick={openBulkUpdateModal}
          title={t('pump_batch_update_title')}
        >
          <span className="fab-badge">{selectedPumpIds.length > 99 ? '99+' : selectedPumpIds.length}</span>
          <i className="fas fa-ellipsis-h" aria-hidden />
        </button>
      )}

      <BulkUpdateStatusModal
        isOpen={showBulkUpdateModal}
        pumpIds={selectedPumpIds}
        onClose={() => {
          setShowBulkUpdateModal(false);
          setBatchFabAnchorRect(null);
        }}
        onSuccess={() => {
          setShowBulkUpdateModal(false);
          setBatchFabAnchorRect(null);
          handleExitSelectMode();
        }}
        anchorRect={batchFabAnchorRect}
      />

      {/* Events Modal - triggered by URL */}
      {pumpId && (
        <PumpEventsModal
          isOpen={showEventsModal}
          pumpId={pumpId}
          onClose={() => navigate(`/dashboard/pumps/${pumpId}`)}
        />
      )}

    </div>
  );
}

